import { graphql, useStaticQuery } from 'gatsby'
import { ImageItem } from '@kentico/gatsby-kontent-components/dist/image-element'
import { filterElementDataByLanguage } from '../../common/serviceHelpers'
import { DynamicSitesPath } from '../../common/typings/enums'
import _get from 'lodash/get'

/* Kentico model: BlogArticles Section*/
export interface KenticoBlogArticlesSection extends KontentItem {
  blogArticles: KenticoBlogArticlesSectionElement[]
}

/* Kentico model: BlogArticle Section Element*/
export interface KenticoBlogArticlesSectionElement extends KontentItem {
  listing_image_or_video: KontentAssetElement,
  listing_parallax: KontentAssetElement[],
  title: KontentTextElement,
  date: KontentDateElement,
  slugs: KontentTextElement,
  blog_category: KontentTextElement
}

export class BlogArticlesSectionElement {
  image: ImageItem
  videoUrl: string
  parallax: ImageItem[]
  title: string
  date: Date
  target: string
  category: string
  authorId: string

  constructor(data: KenticoBlogArticlesSectionElement) {
    const listingAssetType = _get(data, 'listing_image_or_video.value[0].type', '')
    this.image = listingAssetType.includes('image') ? _get(data, 'listing_image_or_video.value[0]') : ''
    this.videoUrl = listingAssetType.includes('video') ? _get(data, 'listing_image_or_video.value[0].url') : ''
    this.parallax = _get(data, 'listing_parallax.value', [])
    this.title = _get(data, 'title.value', '')
    this.date = new Date(_get(data, 'publication_date.value', ''))
    this.target = _get(data, 'slugs.value', '')
    this.category = _get(data, 'blog_category.value[0].name', '')
    this.authorId = _get(data, 'article_author.value[0].id', '')
  }
}

export class BlogArticlesSection {
  blogArticles: BlogArticlesSectionElement[]

  constructor(data: KenticoBlogArticlesSectionElement[]) {
    this.blogArticles = data
      .filter(Boolean)
      .map((element: KenticoBlogArticlesSectionElement) => {
        return new BlogArticlesSectionElement(element)
      })
    this.sortArticlesByDate()
  }

  private getTime(date?: Date) {
    return date != null ? date.getTime() : 0
  }

  private sortArticlesByDate(): void {
    this.blogArticles.sort((a: BlogArticlesSectionElement, b: BlogArticlesSectionElement) => {
      return this.getTime(b.date) - this.getTime(a.date)
    })
  }
}

export const blogArticlesSectionFragment = graphql`
  query BlogArticlesSectionQuery {
    allKontentItemBlogArticle {
      ...kontent_item_blog_articleConnectionFragment
    }
  }
`

export const blogArticlesSectionNodesFragment = graphql`
  fragment kontent_item_blog_articleConnectionFragment on kontent_item_blog_articleConnection {
    nodes {
      id
      preferred_language
      elements {
        listing_image_or_video {
          value {
            url
            description
            height
            name
            size
            type
            width
          }
        }
        listing_parallax {
          value {
            url
            description
            height
            name
            size
            type
            width
          }
        }
        title {
          value
        }
        publication_date {
          value
        }
        slugs {
          value
        }
        blog_category {
          value {
            name
          }
        }
        article_author {
          value {
            id
          }
        }
      }
    }
  }
`

export const useBlogArticlesSectionData = (language: string) => {
  const data = useStaticQuery(blogArticlesSectionFragment)
  const blogArticleSectionData: KenticoBlogArticlesSectionElement[] =
    filterElementDataByLanguage(data, language, DynamicSitesPath.BLOG).map((blogArticle: any) => blogArticle.elements)
  return new BlogArticlesSection(blogArticleSectionData)
}